import { Price } from '@jouzen/ecom-components/v3/Price';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import Link from 'next/link';
import { useLocale, useTranslations } from 'next-intl';
import type { PropsWithChildren } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

import FeatureGate from '@/app/components/FeatureGate';
import Image from '@/app/components/Image';
import { useProductData } from '@/queries/Product';
import getLocalePartialUrl from '@/utils/getLocalePartialUrl';

export interface ProductRecommendationCardProps {
  readonly productKey: string;
  readonly title: MessageKey;
  readonly description: MessageKey;
  readonly image: {
    readonly alt: MessageKey;
    readonly src: string;
  };
  readonly urlPath: string;
  readonly dataCy?: string;
  readonly featureGate?: string | undefined;
}

const Wrapper = ({
  children,
  featureGate,
}: PropsWithChildren<Pick<ProductRecommendationCardProps, 'featureGate'>>) => {
  if (featureGate) {
    return <FeatureGate id={featureGate}>{children}</FeatureGate>;
  }
  return children;
};

export const ProductRecommendationCard = ({
  productKey,
  title,
  description,
  image,
  urlPath,
  dataCy,
  featureGate,
}: ProductRecommendationCardProps) => {
  const t = useTranslations();
  const locale = useLocale();
  const localePartialUrl = getLocalePartialUrl(locale);

  const { data: product } = useProductData({
    productKey,
    includeInventory: false,
  });

  const price = product?.price;

  return (
    <Wrapper featureGate={featureGate}>
      <Link
        className="flex w-full flex-row items-center gap-3 rounded-lg border border-gray-300 px-4.5 py-4 hover:border-gray-400"
        data-cy={dataCy}
        href={`${localePartialUrl}${urlPath}`}
      >
        <Image
          className="h-16.75 w-18.75 shrink-0 grow-0"
          format="png"
          height={67}
          localizedAlt={image.alt}
          src={image.src}
          width={75}
        />
        <div className="shrink grow">
          <Typography className="text-base font-bold" Element="h3">
            {t(title)}
          </Typography>
          <Typography className="text-sm font-normal">
            {t(description)}
          </Typography>
          {price?.amount && price.currencyCode && (
            <Price
              className="mt-1 text-sm font-normal text-gray-400"
              currencyCode={price.currencyCode}
              data-cy="product-recommendation-price"
              price={price.amount}
            />
          )}
        </div>
      </Link>
    </Wrapper>
  );
};
